/**
 * Mock Job Types Data
 *
 * Sample job type definitions with custom field configurations.
 * Used for development and testing until backend API is integrated.
 */

export type FieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'boolean';

export interface ValidationRule {
  type: 'required' | 'min' | 'max' | 'pattern' | 'minLength' | 'maxLength';
  value: string | number | boolean;
  message: string;
}

export interface CustomFieldDefinition {
  name: string;
  label: string;
  fieldType: FieldType;
  defaultValue?: string | number | boolean;
  options?: string[];
  validationRules?: ValidationRule[];
  helpText?: string;
  displayOrder: number;
  isRequired: boolean;
  isActive: boolean;
}

export interface JobType {
  id: string;
  providerId: string;
  name: string;
  description: string;
  isActive: boolean;
  fieldDefinitions: CustomFieldDefinition[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Mock job types with realistic configurations
 */
export const mockJobTypes: JobType[] = [
  {
    id: 'jt-rolloff-1',
    providerId: 'prov-1',
    name: 'Rolloff Container',
    description: 'Standard roll-off dumpster rental and hauling service',
    isActive: true,
    fieldDefinitions: [
      {
        name: 'permit_number',
        label: 'Permit Number',
        fieldType: 'text',
        helpText: 'Required permit number for placement',
        displayOrder: 1,
        isRequired: false,
        isActive: true,
      },
      {
        name: 'haul_distance_miles',
        label: 'Haul Distance (miles)',
        fieldType: 'number',
        helpText: 'Distance from site to disposal facility',
        displayOrder: 2,
        isRequired: false,
        isActive: true,
        validationRules: [
          {
            type: 'min',
            value: 0,
            message: 'Distance must be positive',
          },
        ],
      },
      {
        name: 'placement_notes',
        label: 'Placement Notes',
        fieldType: 'text',
        helpText: 'Special instructions for container placement',
        displayOrder: 3,
        isRequired: false,
        isActive: true,
      },
      {
        name: 'estimated_tonnage',
        label: 'Estimated Tonnage',
        fieldType: 'number',
        helpText: 'Estimated weight of material',
        displayOrder: 4,
        isRequired: false,
        isActive: true,
        validationRules: [
          {
            type: 'min',
            value: 0,
            message: 'Tonnage must be positive',
          },
        ],
      },
    ],
    createdAt: new Date('2025-01-01').toISOString(),
    updatedAt: new Date('2025-01-01').toISOString(),
  },
  {
    id: 'jt-demolition-1',
    providerId: 'prov-1',
    name: 'Demolition Cleanup',
    description: 'Demolition and C&D waste removal service',
    isActive: true,
    fieldDefinitions: [
      {
        name: 'permit_number',
        label: 'Demolition Permit',
        fieldType: 'text',
        helpText: 'Required demolition permit number',
        displayOrder: 1,
        isRequired: true,
        isActive: true,
      },
      {
        name: 'hazmat_present',
        label: 'Hazmat Present',
        fieldType: 'boolean',
        helpText: 'Check if hazardous materials are present',
        displayOrder: 2,
        isRequired: true,
        isActive: true,
        defaultValue: false,
      },
      {
        name: 'access_restrictions',
        label: 'Access Restrictions',
        fieldType: 'multiselect',
        helpText: 'Select all applicable access restrictions',
        options: [
          'Narrow street',
          'Low clearance',
          'Residential area',
          'Permit required',
          'Time restrictions',
        ],
        displayOrder: 3,
        isRequired: false,
        isActive: true,
      },
      {
        name: 'structure_type',
        label: 'Structure Type',
        fieldType: 'select',
        helpText: 'Type of structure being demolished',
        options: [
          'Residential house',
          'Commercial building',
          'Industrial facility',
          'Shed/Garage',
          'Other',
        ],
        displayOrder: 4,
        isRequired: true,
        isActive: true,
      },
      {
        name: 'demolition_date',
        label: 'Scheduled Demolition Date',
        fieldType: 'date',
        helpText: 'Date when demolition will occur',
        displayOrder: 5,
        isRequired: false,
        isActive: true,
      },
    ],
    createdAt: new Date('2025-01-01').toISOString(),
    updatedAt: new Date('2025-01-01').toISOString(),
  },
  {
    id: 'jt-green-waste-1',
    providerId: 'prov-1',
    name: 'Green Waste Collection',
    description: 'Yard waste and organic material collection',
    isActive: true,
    fieldDefinitions: [
      {
        name: 'material_type',
        label: 'Material Type',
        fieldType: 'select',
        helpText: 'Type of green waste',
        options: [
          'Yard trimmings',
          'Tree debris',
          'Brush',
          'Leaves',
          'Mixed green waste',
        ],
        displayOrder: 1,
        isRequired: true,
        isActive: true,
      },
      {
        name: 'estimated_volume',
        label: 'Estimated Volume (cubic yards)',
        fieldType: 'number',
        helpText: 'Approximate volume of material',
        displayOrder: 2,
        isRequired: false,
        isActive: true,
        validationRules: [
          {
            type: 'min',
            value: 0,
            message: 'Volume must be positive',
          },
        ],
      },
      {
        name: 'seasonal_discount_applied',
        label: 'Seasonal Discount Applied',
        fieldType: 'boolean',
        helpText: 'Check if seasonal pricing applies',
        displayOrder: 3,
        isRequired: false,
        isActive: true,
        defaultValue: false,
      },
    ],
    createdAt: new Date('2025-01-01').toISOString(),
    updatedAt: new Date('2025-01-01').toISOString(),
  },
  {
    id: 'jt-cd-recycling-1',
    providerId: 'prov-1',
    name: 'C&D Recycling',
    description: 'Construction and demolition material recycling',
    isActive: true,
    fieldDefinitions: [
      {
        name: 'material_breakdown',
        label: 'Material Breakdown',
        fieldType: 'multiselect',
        helpText: 'Select all material types present',
        options: [
          'Wood',
          'Concrete',
          'Metal',
          'Drywall',
          'Asphalt',
          'Cardboard',
          'Plastic',
        ],
        displayOrder: 1,
        isRequired: true,
        isActive: true,
      },
      {
        name: 'recycling_goal_percentage',
        label: 'Recycling Goal (%)',
        fieldType: 'number',
        helpText: 'Target percentage of material to recycle',
        displayOrder: 2,
        isRequired: false,
        isActive: true,
        defaultValue: 75,
        validationRules: [
          {
            type: 'min',
            value: 0,
            message: 'Percentage must be between 0 and 100',
          },
          {
            type: 'max',
            value: 100,
            message: 'Percentage must be between 0 and 100',
          },
        ],
      },
      {
        name: 'sorting_required',
        label: 'On-Site Sorting Required',
        fieldType: 'boolean',
        helpText: 'Check if materials need to be sorted on-site',
        displayOrder: 3,
        isRequired: false,
        isActive: true,
        defaultValue: false,
      },
      {
        name: 'notes',
        label: 'Recycling Notes',
        fieldType: 'text',
        helpText: 'Additional notes about recycling requirements',
        displayOrder: 4,
        isRequired: false,
        isActive: true,
      },
    ],
    createdAt: new Date('2025-01-01').toISOString(),
    updatedAt: new Date('2025-01-01').toISOString(),
  },
];

/**
 * Get all active job types
 */
export function getMockActiveJobTypes(): JobType[] {
  return mockJobTypes.filter((jt) => jt.isActive);
}

/**
 * Get job type by ID
 */
export function getMockJobTypeById(id: string): JobType | undefined {
  return mockJobTypes.find((jt) => jt.id === id);
}

/**
 * Get job type by name
 */
export function getMockJobTypeByName(name: string): JobType | undefined {
  return mockJobTypes.find((jt) => jt.name === name);
}
